<template>
<div class="dropdown-modal-component" :id="modalId" :class="{ active: active }">
  <div class="dropdown-modal-toggle" @click="toggleDropdownModal">
    <slot name="toggle"></slot>
  </div>
  <div class="dropdown-modal-content" v-show="active">
    <slot name="content"></slot>
    <div v-if="arrow" class="dropdown-modal-arrow"></div>
  </div>
</div>
</template>

<script>
import { createPopper } from '@popperjs/core';
import { mapMutations } from 'vuex'

export default {
  $modal: null,
  $modalRoot: null,
  $modalContent: null,

  props: {
    active: {
      type: Boolean,
    },

    placement: {
      type: String,
      required: false,
      default: 'bottom',
    },

    strategy: { type: String, required: false },

    arrow: {
      type: Boolean,
      required: false,
      default: true,
    },

    overlay: {
      type: Boolean,
      required: false,
      default: true,
    },

    closeOnClick: {
      type: Boolean,
      required: false,
      default: true,
    },

    offset: {
      type: Array,
      required: false,
      default: 0,
    },
  },

  created() {
    this.$on('dismiss', () => this.$emit('update:active', false))
  },

  mounted() {
    this.createModalInstance()
  },

  beforeDestroy() {
    if (this.overlay && this.active) {
      this.unregisterDropdownModal(this);
    }
  },

  methods: {
    ...mapMutations({
      registerDropdownModal: 'ui/registerDropdownModal',
      unregisterDropdownModal: 'ui/unregisterDropdownModal',
    }),

    toggleDropdownModal() {
      this.$emit('update:active', !this.active)
    },

    registerDocumentEvents() {
      document.addEventListener('click', this.documentClickHandler)
    },

    unregisterDocumentEvents() {
      document.removeEventListener('click', this.documentClickHandler)
    },

    documentClickHandler(evt) {
      if (!evt.target || !this.$modalContent || this.$modalContent.contains(evt.target)) {
        return
      }

      this.toggleDropdownModal()
    },

    createModalInstance() {
      if (this.modalWasCreated) {
        return this.$modal
      }

      this.$modalRoot = document.getElementById(this.modalId)

      this.$modal = createPopper(
        this.$modalRoot.querySelector('.dropdown-modal-toggle'),
        this.$modalContent = this.$modalRoot.querySelector('.dropdown-modal-content'),
        this.modalOptions(
          this.arrow ? this.$modalRoot.querySelector('.dropdown-modal-arrow') : null
        )
      )

      this.modalWasCreated = true

      return this.$modal
    },

    modalOptions(arrowElement) {
      const obj = {
        placement: this.placement,
        modifiers: [
          {
            name: 'offset',
            enabled: true,
            options: {
              offset: this.offset,
            },
          },
          {
            name: 'arrow',
            enabled: this.arrow,
            options: {
              element: arrowElement,
              padding: 5,
            },
          },
        ],
      };
      if (this.strategy) obj.strategy = this.strategy;
      return obj;
    },
  },

  watch: {
    active: {
      immediate: true,
      handler(val) {
        if (val) {
          requestAnimationFrame(() =>
            requestAnimationFrame(() => {
              this.$modal.update()
              this.registerDocumentEvents()
            })
          );
        } else {
          this.unregisterDocumentEvents()
        }

        if (this.overlay) {
          if (val) {
            this.registerDropdownModal(this)
          } else {
            this.unregisterDropdownModal(this)
          }
        }
      },
    }
  },

  data() {
    return {
      modalId: `dropdown-modal-${Math.random().toString().replace('.', '_')}`,
      modalWasCreated: false,
    }
  },
}
</script>

<style lang="less">
.dropdown-modal-component {
  @apply inline;

  .dropdown-modal-content {
    @apply bg-white rounded shadow-md relative z-dropdown-modal;

    .dropdown-modal-arrow {
      @apply absolute border-solid border-white w-0 h-0;
    }

    &[data-popper-placement^="top"] {
      .dropdown-modal-arrow {
        @apply my-0;

        border-width: theme('customVars.popover.arrowSize') theme('customVars.popover.arrowSize') 0 theme('customVars.popover.arrowSize');
        border-left-color: transparent;
        border-right-color: transparent;
        border-bottom-color: transparent;
        bottom: theme('customVars.popover.negativeArrowSize');
        left: calc(50% - theme('customVars.popover.arrowSize'));
      }
    }

    &[data-popper-placement^="bottom"] {
      .dropdown-modal-arrow {
        @apply my-0;

        border-width: 0 theme('customVars.popover.arrowSize') theme('customVars.popover.arrowSize') theme('customVars.popover.arrowSize');
        border-left-color: transparent;
        border-right-color: transparent;
        border-top-color: transparent;
        top: theme('customVars.popover.negativeArrowSize');
        left: calc(50% - theme('customVars.popover.arrowSize'));
      }
    }

    &[data-popper-placement^="right"] {
      .dropdown-modal-arrow {
        @apply mx-0;
        border-width: theme('customVars.popover.arrowSize') theme('customVars.popover.arrowSize') theme('customVars.popover.arrowSize') 0;
        border-left-color: transparent;
        border-top-color: transparent;
        border-bottom-color: transparent;
        left: theme('customVars.popover.negativeArrowSize');
        top: calc(50% - theme('customVars.popover.arrowSize'));
      }
    }

    &[data-popper-placement^="left"] {
      .dropdown-modal-arrow {
        @apply mx-0;
        border-width: theme('customVars.popover.arrowSize') 0 theme('customVars.popover.arrowSize') theme('customVars.popover.arrowSize');
        border-top-color: transparent;
        border-right-color: transparent;
        border-bottom-color: transparent;
        right: theme('customVars.popover.negativeArrowSize');
        top: calc(50% - theme('customVars.popover.arrowSize'));
      }
    }
  }
}
</style>
